<template>

  <div class="row">
    <div class="col-12">
      <div
          v-for="group in groupedSettings"
          :key="group.key"
          class="settings-group"
      >
        <div class="settings-group__header">
          <span class="settings-group__title">{{ group.displayName }}</span>
        </div>
        <div class="settings-group__body">

          <div class="common-settings-editor">
            <div v-if="title || description" class="mb-3">
              <h5 v-if="title" class="mb-1">{{ title }}</h5>
              <p v-if="description" class="text-muted mb-0">{{ description }}</p>
            </div>
            <div class="settings-list" :class="{ 'settings-list--dense': dense }" :style="listStyles">
              <div v-for="setting in group.settings" :key="setting.id" class="settings-row">
                <div class="row align-items-center mx-0 gx-2">
                  <div class="col-md-1">
                    <div>{{ setting.position }}.</div>
                  </div>
                  <div class="col-md-6">
                    <div>{{ setting.displayedName }}</div>
                  </div>
                  <div class="col-md-3">
                    <div class="settings-input">
                      <div v-if="setting.settingType === 'BOOLEAN'" class="form-check form-switch">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            :id="setting.name"
                            v-model="localValues[setting.name]"
                            :disabled="readOnly"
                        />
                      </div>
                      <template v-else-if="setting.settingType === 'DOUBLE'">
                        <input
                            class="form-control"
                            :class="[inputClass, { 'is-invalid': isInvalid(setting) }]"
                            type="number"
                            min="0.00"
                            step="0.01"
                            :id="setting.name"
                            v-model="localValues[setting.name]"
                            :disabled="readOnly"
                        />
                        <div v-if="isInvalid(setting)" class="invalid-feedback">
                          Wprowadź liczbę dziesiętną.
                        </div>
                      </template>
                      <template v-else-if="setting.settingType === 'INTEGER'">
                        <input
                            class="form-control"
                            :class="[inputClass, { 'is-invalid': isInvalid(setting) }]"
                            type="number"
                            step="1"
                            :id="setting.name"
                            v-model="localValues[setting.name]"
                            :disabled="readOnly"
                        />
                        <div v-if="isInvalid(setting)" class="invalid-feedback">
                          Wprowadź liczbę całkowitą.
                        </div>
                      </template>
                      <template v-else-if="setting.settingType === 'ARRAY'">
                        <input
                            class="form-control"
                            :class="inputClass"
                            type="text"
                            :id="setting.name"
                            v-model="localValues[setting.name]"
                            :disabled="readOnly"
                        />
                      </template>
                      <template v-else>
                        <input
                            class="form-control"
                            :class="inputClass"
                            :type="setting.encrypted ? 'password' : 'text'"
                            :id="setting.name"
                            v-model="localValues[setting.name]"
                            :disabled="readOnly"
                        />
                      </template>
                    </div>
                  </div>
                  <div class="col-md-2 text-end">
                    <button
                        class="btn btn-outline-primary btn-sm"
                        type="button"
                        @click="openOverrides(setting)"
                        title="Nadpisz"
                    >
                      <i class="fas fa-copy me-1"></i> Nadpisz
                    </button>
                  </div>
                </div>
                <hr class="my-2"/>
              </div>
            </div>

          </div>

        </div>
        <div v-if="group.isSpecial" class="settings-group__footer d-flex justify-content-end">
          <button
              class="btn btn-outline-success btn-sm"
              type="button"
              @click="emitTestSettings(group)"
          >
            <i class="fas fa-vial me-1"></i> {{ testButtonTitle }}
          </button>

        </div>
      </div>
    </div>
  </div>


  <SettingOverridesModal
      v-if="overridesModalVisible"
      :show-modal="overridesModalVisible"
      :selected-setting="selectedSettingForOverride"
      @update:showModal="overridesModalVisible = $event"
      @onClose="overridesModalVisible = false"
  />
</template>

<script>
import {settingsHelperFunctions} from "@/helpers/settingsHelper";
import SettingOverridesModal from "@/views/DrugstoreCommonManager/SettingOverridesModal.vue";

const DEFAULT_DOUBLE_VALUE = settingsHelperFunctions.getRoundedValue(0);
const DEFAULT_INTEGER_VALUE = "0";
const FALLBACK_GROUP_KEY = "__other__";
const FALLBACK_GROUP_DISPLAY = "Inne ustawienia";


export default {
  name: "CommonSettingsEditor",
  props: {
    settings: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    saveButtonText: {
      type: String,
      default: "Save",
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: true,
    },
    maxHeight: {
      type: String,
      default: "65vh",
    },
    testButtonTitle: {
      type: String,
      default: "Testuj",
    }
  },
  emits: ["onSaveSettings", "onTestSettingsGroup"],
  components: {
    SettingOverridesModal,
  },
  data() {
    return {
      localValues: {},
      overridesModalVisible: false,
      selectedSettingForOverride: null,
    };
  },
  computed: {

    inputClass() {
      return this.dense ? "form-control-sm" : "";
    },
    listStyles() {
      return {
        maxHeight: this.maxHeight,
        overflowY: "auto",
      };
    },
    groupedSettings() {
      const groupMap = new Map();

      this.settings.forEach((setting) => {
        const rawKey = setting.settingGroup && setting.settingGroup.trim()
            ? setting.settingGroup.trim()
            : FALLBACK_GROUP_KEY;

        if (!groupMap.has(rawKey)) {
          const isSpecial = rawKey.includes("*");
          const displayName =
              rawKey === FALLBACK_GROUP_KEY
                  ? FALLBACK_GROUP_DISPLAY
                  : rawKey;

          groupMap.set(rawKey, {
            key: rawKey,
            displayName,
            isSpecial,
            settings: [],
          });
        }

        groupMap.get(rawKey).settings.push(setting);
      });

      return Array.from(groupMap.values())
          .map((group) => ({
            ...group,
            settings: [...group.settings].sort((a, b) => {
              const posA = a.position ?? Number.MAX_SAFE_INTEGER;
              const posB = b.position ?? Number.MAX_SAFE_INTEGER;
              return posA - posB;
            }),
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName, 'pl', {sensitivity: 'base'}));
    },
  },
  watch: {
    settings: {
      handler(newSettings) {
        this.initializeValues(newSettings);
      },
      immediate: true,
    },
  },
  methods: {
    initializeValues(settings) {
      const values = {};
      (settings || []).forEach((setting) => {
        values[setting.name] = this.getInitialValue(setting);
      });
      this.localValues = values;
    },
    getInitialValue(setting) {
      if (setting.settingType === "BOOLEAN") {
        if (setting.value === null || setting.value === undefined) {
          return false;
        }
        if (typeof setting.value === "boolean") {
          return setting.value;
        }
        return setting.value.toString().toUpperCase() !== "FALSE";
      }
      if (setting.settingType === "DOUBLE") {
        if (setting.value === null || setting.value === undefined) {
          return DEFAULT_DOUBLE_VALUE;
        }
        return settingsHelperFunctions.getRoundedValue(setting.value);
      }
      if (setting.settingType === "INTEGER") {
        if (setting.value === null || setting.value === undefined) {
          return DEFAULT_INTEGER_VALUE;
        }
        return parseInt(setting.value, 10).toString();
      }
      if (setting.settingType === "ARRAY") {
        if (setting.value === null || setting.value === undefined) {
          return "";
        }
        const rawArrayValue = setting.value.toString();
        const startIndex = rawArrayValue.indexOf("[");
        const endIndex = rawArrayValue.lastIndexOf("]");
        if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
          return rawArrayValue.substring(startIndex + 1, endIndex);
        }
        return rawArrayValue;
      }
      if (setting.value === null || setting.value === undefined || setting.value === "null") {
        return "";
      }
      return setting.value.toString();
    },
    isInvalid(setting) {
      const value = this.localValues[setting.name];
      if (setting.settingType === "DOUBLE") {
        return settingsHelperFunctions.valueIsNotDouble(value);
      }
      if (setting.settingType === "INTEGER") {
        return settingsHelperFunctions.valueIsNotInt(value);
      }
      return false;
    },
    emitSave() {
      this.$emit("onSaveSettings", {
        settings: this.settings,
        settingsInputValuesMap: this.getSettingsValueMap(),
      });
    },
    emitTestSettings(group) {
      this.$emit('onTestSettingsGroup', { key: group.key,
        settingsToSave:{
          settings: this.settings,
          settingsInputValuesMap: this.getSettingsValueMap(),
        }
      })
    },

    getSettingsValueMap() {
      const settingsInputValuesMap = new Map();
      (this.settings || []).forEach((setting) => {
        const value = this.localValues[setting.name];
        const booleanValue = setting.settingType === "BOOLEAN" ? Boolean(value) : undefined;
        settingsInputValuesMap.set(setting.name, {
          value,
          checked: booleanValue,
        });
      });
      return settingsInputValuesMap;
    },
    openOverrides(setting) {
      this.selectedSettingForOverride = setting;
      this.overridesModalVisible = true;
    },
  },
};
</script>

<style scoped>
.settings-list {
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

@media (max-width: 768px) {
  .settings-list {
    overflow-x: auto;
  }
}
.settings-row {
  padding: 0.35rem 0;
}

.settings-list--dense {
  font-size: 0.9em;
}

.settings-group {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 16px;
  background-color: #ffffff;
  overflow: hidden;
}

.settings-group__header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 14px;
}

.settings-group__title {
  font-size: 0.85em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #495057;
}

.settings-group__body {
  padding: 10px 14px 4px 14px;
}

.settings-group__footer {
  padding: 8px 14px 10px 14px;
  border-top: 1px solid #f0f0f0;
  background-color: #fafafa;
}

.slim-style {
  font-size: 0.9em !important;
}
</style>
